import { useCallback, useState } from 'react';
import { DateRangePicker } from '../../components/DateRangePicker';
import { GuestPicker } from '../../components/GuestPicker';
import { useDropdownGroup } from '../../hooks/useDropdownGroup';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import { DotIcon } from '../../icons/DotIcon';
import { SearchSmallIcon } from '../../icons/SearchSmallIcon';
import { formatDateShort, formatDateWithWeekday } from '../../utils/dateHelpers';
import {
  Bar,
  CategoryDropdown,
  CategoryOption,
  CategoryWrapper,
  ChevronIcon,
  DatesWrapper,
  DotSeparator,
  DropdownAnchor,
  GuestsWrapper,
  Pill,
  SearchLabel,
  SearchPill,
  SearchWrapper,
} from './JourneyPicker.styles';
import type { JourneyPickerProps } from './JourneyPicker.types';

function buildDatesLabel(
  fromDate: string,
  toDate: string,
  singleMode: boolean,
  selectDate: string,
  selectDates: string
): string {
  if (singleMode) {
    if (!fromDate) return selectDate;
    return formatDateWithWeekday(fromDate);
  }
  if (!fromDate && !toDate) return selectDates;
  if (fromDate && !toDate) return formatDateShort(fromDate);

  const fromDay = fromDate.split('-')[2].replace(/^0/, '');
  const to = formatDateShort(toDate);

  const fromMonth = fromDate.slice(5, 7);
  const toMonth = toDate.slice(5, 7);
  if (fromMonth === toMonth && fromDate.slice(0, 4) === toDate.slice(0, 4)) {
    return `${fromDay} - ${to}`;
  }

  return `${formatDateShort(fromDate)} - ${to}`;
}

const DROPDOWN_KEYS = ['category', 'dates', 'guests'] as const;
type DropdownKey = (typeof DROPDOWN_KEYS)[number];

export function JourneyPicker({
  category,
  categories,
  onCategoryChange,
  disableCategorySelect,
  dateMode = 'range',
  showGuests,
  defaultFromDate = '',
  defaultToDate = '',
  defaultAdults = 2,
  defaultChildren = 0,
  availability,
  isLoadingAvailability,
  onMonthChange,
  columnView,
  onSearch,
  labels: l,
}: JourneyPickerProps) {
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);
  const [adults, setAdults] = useState(defaultAdults);
  const [children, setChildren] = useState(defaultChildren);

  const { toggle, closeAll, setRef, isOpen } = useDropdownGroup<DropdownKey>(DROPDOWN_KEYS);

  const isSingleDate = dateMode === 'single';
  const displayGuests = showGuests !== undefined ? showGuests : !isSingleDate;

  const handleRangeSelect = useCallback(
    (checkIn: string, checkOut: string) => {
      setFromDate(checkIn);
      if (isSingleDate) {
        setToDate('');
        closeAll();
      } else {
        setToDate(checkOut);
        if (checkOut) closeAll();
      }
    },
    [isSingleDate, closeAll]
  );

  const handleAdultsChange = useCallback((val: number) => {
    setAdults(val);
  }, []);

  const handleChildrenChange = useCallback((val: number) => {
    setChildren(val);
  }, []);

  const selectDateLabel = l?.selectDate ?? 'Select date';
  const selectDatesLabel = l?.selectDates ?? 'Select dates';
  const searchLabel = l?.search ?? 'Search';
  const browseLabel = l?.browse ?? 'Browse';
  const defaultGuestsLabel = (total: number) => `${total} Guest${total !== 1 ? 's' : ''}`;

  const datesLabel = buildDatesLabel(
    fromDate,
    toDate,
    isSingleDate,
    selectDateLabel,
    selectDatesLabel
  );
  const guestsLabel = (l?.guests ?? defaultGuestsLabel)(adults + children);

  const showCategory = !disableCategorySelect && categories && categories.length > 0;
  const currentCategoryLabel = categories?.find((c) => c.id === category)?.label ?? browseLabel;

  const handleSearchClick = useCallback(() => {
    closeAll();
    if (isSingleDate) {
      if (fromDate) onSearch({ fromDate, adults, children });
    } else {
      if (fromDate && toDate) onSearch({ fromDate, toDate, adults, children });
    }
  }, [isSingleDate, fromDate, toDate, adults, children, onSearch, closeAll]);

  return (
    <Bar>
      {showCategory && (
        <>
          <CategoryWrapper ref={setRef('category')}>
            <Pill type="button" onClick={() => toggle('category')}>
              {currentCategoryLabel}
              <ChevronIcon>
                <ChevronDownIcon />
              </ChevronIcon>
            </Pill>

            {isOpen('category') && (
              <CategoryDropdown>
                {categories!.map((cat) => (
                  <CategoryOption
                    key={cat.id}
                    type="button"
                    $active={cat.id === category}
                    onClick={() => {
                      closeAll();
                      onCategoryChange?.(cat.id);
                    }}
                  >
                    {cat.label}
                  </CategoryOption>
                ))}
              </CategoryDropdown>
            )}
          </CategoryWrapper>
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>
        </>
      )}

      <DatesWrapper ref={setRef('dates')} $isFirst={!showCategory}>
        <Pill type="button" onClick={() => toggle('dates')}>
          {datesLabel}
        </Pill>

        {isOpen('dates') && (
          <DropdownAnchor>
            <DateRangePicker
              checkIn={fromDate || null}
              checkOut={isSingleDate ? null : toDate || null}
              singleDateMode={isSingleDate}
              availability={availability}
              isLoading={isLoadingAvailability}
              onRangeSelect={handleRangeSelect}
              onMonthChange={onMonthChange}
              columnView={columnView}
              renderInline
              labels={l?.datePicker}
            />
          </DropdownAnchor>
        )}
      </DatesWrapper>

      {displayGuests && (
        <>
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>

          <GuestsWrapper ref={setRef('guests')}>
            <Pill type="button" onClick={() => toggle('guests')}>
              {guestsLabel}
            </Pill>

            {isOpen('guests') && (
              <DropdownAnchor>
                <GuestPicker
                  adults={adults}
                  childCount={children}
                  onAdultsChange={handleAdultsChange}
                  onChildrenChange={handleChildrenChange}
                  renderInline
                  labels={l?.guestPicker}
                />
              </DropdownAnchor>
            )}
          </GuestsWrapper>
        </>
      )}
      <DotSeparator aria-hidden>
        <DotIcon />
      </DotSeparator>
      <SearchWrapper>
        <SearchPill type="button" onClick={handleSearchClick}>
          <SearchSmallIcon />
          <SearchLabel data-text={searchLabel}>{searchLabel}</SearchLabel>
        </SearchPill>
      </SearchWrapper>
    </Bar>
  );
}
